import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import {
  CHAPTERS,
  LOOP_DURATION_MS,
  chapterIndexFromTime,
  chapterProgress,
  type ChapterId,
} from './heroTheaterData';

function chapterRepresentativeMs(index: number): number {
  const chapter = CHAPTERS[(index + CHAPTERS.length) % CHAPTERS.length];
  return chapter.startMs + (chapter.endMs - chapter.startMs) * 0.55;
}

export interface HeroTheaterTimeline {
  /** Elapsed ms within the current loop (0 … LOOP_DURATION_MS) */
  timeMs: number;
  chapterIndex: number;
  chapterId: ChapterId;
  /** 0–1 progress within the active chapter */
  progress: number;
  reducedMotion: boolean;
  isPaused: boolean;
  jumpToChapter: (index: number) => void;
  containerRef: RefObject<HTMLDivElement>;
}

/** Tailwind `md` — below this, hold scenes until the user scrolls. */
const MOBILE_MAX_MQ = '(max-width: 767px)';

function isScrollUnlocked(): boolean {
  if (typeof window === 'undefined') return false;
  return !window.matchMedia(MOBILE_MAX_MQ).matches || window.scrollY > 0;
}

export function useHeroTheaterTimeline(): HeroTheaterTimeline {
  const containerRef = useRef<HTMLDivElement>(null);
  const [timeMs, setTimeMs] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isTabVisible, setIsTabVisible] = useState(true);
  const [scrollUnlocked, setScrollUnlocked] = useState(isScrollUnlocked);

  const startRef = useRef<number | null>(null);
  const offsetRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const reducedMotionRef = useRef(false);

  const isPaused = !isVisible || !isTabVisible || reducedMotion || !scrollUnlocked;

  const jumpToChapter = useCallback((index: number) => {
    const chapter = CHAPTERS[(index + CHAPTERS.length) % CHAPTERS.length];
    const target = reducedMotionRef.current
      ? chapterRepresentativeMs(index)
      : chapter.startMs;
    offsetRef.current = target;
    startRef.current = null;
    setTimeMs(target);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => {
      const value = mq.matches;
      reducedMotionRef.current = value;
      setReducedMotion(value);
    };
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  // Mobile: keep scenes frozen at the opening frame until the user scrolls.
  useEffect(() => {
    if (scrollUnlocked) return;

    const mobileMq = window.matchMedia(MOBILE_MAX_MQ);

    const unlock = () => {
      if (isScrollUnlocked()) setScrollUnlocked(true);
    };

    unlock();
    if (isScrollUnlocked()) return;

    window.addEventListener('scroll', unlock, { passive: true });
    mobileMq.addEventListener('change', unlock);
    return () => {
      window.removeEventListener('scroll', unlock);
      mobileMq.removeEventListener('change', unlock);
    };
  }, [scrollUnlocked]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting && entry.intersectionRatio > 0.15),
      { threshold: [0, 0.15, 0.5] },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onVisibility = () => setIsTabVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  useEffect(() => {
    if (isPaused) {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      // Freeze clock: capture current offset when pausing
      if (startRef.current != null) {
        offsetRef.current =
          (offsetRef.current + (performance.now() - startRef.current)) % LOOP_DURATION_MS;
        startRef.current = null;
      }
      return;
    }

    const tick = (now: number) => {
      if (startRef.current == null) startRef.current = now;
      const elapsed = (offsetRef.current + (now - startRef.current)) % LOOP_DURATION_MS;
      setTimeMs(elapsed);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isPaused]);

  // When reduced motion turns on, park at a representative frame for the current chapter
  useEffect(() => {
    if (!reducedMotion) return;
    const idx = chapterIndexFromTime(offsetRef.current || timeMs);
    const mid = chapterRepresentativeMs(idx);
    offsetRef.current = mid;
    startRef.current = null;
    setTimeMs(mid);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snap only when reducedMotion enables
  }, [reducedMotion]);

  const chapterIndex = chapterIndexFromTime(timeMs);
  const chapterId = CHAPTERS[chapterIndex].id;
  const progress = chapterProgress(timeMs, chapterIndex);

  return {
    timeMs,
    chapterIndex,
    chapterId,
    progress,
    reducedMotion,
    isPaused,
    jumpToChapter,
    containerRef,
  };
}
